// @owners { team: patient-fulfillment }
import { BORDERS, COLORS, SIZES } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  AltoIcon,
  Column,
  Description,
  Illustration,
  Link,
  ListDescription,
  ListItem,
  Row,
  SmSpacing,
  Tag,
} from '@alto/design-system';
import { type AddOnOtc } from '@alto/scriptdash/alto/patient_app/add_ons/types/v1/add_on_otc';
import { isToday as isDateToday, parse } from 'date-fns';
import React, { useCallback, useContext } from 'react';
import { View } from 'react-native';
import styled from 'styled-components/native';
import { IMG_ALT_TEXTS } from '~shared/constants';
import { getCashLessThanInsuranceSavings, getItemQuantityForAddOnOtc } from '~shared/features/checkout/helpers';
import { getWindows } from '~shared/features/checkout/selectors/getCart';
import getCartAutoRefillForPrescription from '~shared/features/checkout/selectors/getCartAutoRefillForPrescription';
import { type ItemKey } from '~shared/features/checkout/types';
import { getPricingForPrescriptionFromDeliveryItemsForShipment } from '~shared/features/delivery-info/selectors/getDeliveryItems';
import { getUserTagText } from '~shared/features/family-accounts/selectors/getUserTagText';
import { getMedicationForPrescription } from '~shared/features/my-meds/selectors/getMedicationsFromPrescriptions';
import {
  getEarliestAvailableDateForPrescription__DEPRECATED,
  getEarliestAvailableReasonForPrescription__DEPRECATED,
} from '~shared/features/next-available-date/selectors/getNextAvailableDateForPrescription';
import { getPrescriptionByID } from '~shared/features/prescriptions/selectors/getPrescriptionByID';
import { getPriceOptionsForPrescriptionInOrder } from '~shared/features/price-options/selectors/getPriceOptionsForPrescriptionInOrder';
import { PRESCRIPTION_PRICING_LABELS } from '~shared/features/pricing/constants';
import {
  type PriceInfo,
  getCartSelectedPricingInfoForPrescription,
} from '~shared/features/pricing/selectors/getPricingInfoForPrescription';
import { getSavingsAppliedAmountForPrescription } from '~shared/features/pricing/selectors/getSavingsAppliedAmountForPrescription';
import { type Pricing } from '~shared/features/pricing/types';
import { getHasFamily } from '~shared/features/users/selectors/getHasFamily';
import { formatDollars } from '~shared/helpers/currency';
import { DATE_FORMATS, formatRelativeDateWithDow } from '~shared/helpers/date';
import { formatQuantity, isControl } from '~shared/helpers/helper';
import { formatTimeToCutoff, getTimeToCutoff } from '~shared/helpers/timeCutoff';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { useSelectorShared } from '~shared/store';
import { type LightDelivery } from '~shared/types';
import { AncillaryItemsActionSheet } from '../../ancillaries';
import { ORDER_TYPE_CONTEXT, type OrderTypeContext } from '../../auto-refill';
import { BackorderedTag, useBackorderedMedication } from '../../backordered-medications';
import { ManualCouponInfoBox } from '../../coupons';
import { useShipmentID } from '../../delivery-details';
import { NextAvailableDateActionSheet } from '../../next-available-date';
import { EditMedicationItemButton } from './EditMedicationItemButton';
import { PricePendingActionSheet } from './PricePendingActionSheet';
import { RefrigerationActionSheet } from './RefrigerationActionSheet';
import { SignatureRequiredActionSheet } from './SignatureRequiredActionSheet';
import { getMedImageURL } from './helpers';

const PlaceholderContainer = styled(Column).attrs({
  center: true,
})`
  border-radius: ${BORDERS.RADIUS.MD.px};
  overflow: hidden;
  width: ${SIZES.ILLUSTRATION.SM.px};
  height: ${SIZES.ILLUSTRATION.SM.px};
  background-color: ${COLORS.PALETTE.GREYSCALE.DEFAULT};
`;

const AddOnsQuantityTag = styled(View)`
  position: absolute;
  right: 0;
  top: 0;
  z-index: 100;
`;

/**
 * Type predicate helper to narrow the type of a PriceInfo from cart pricing or Pricing from the delivery
 * https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates
 */
const isPriceInfo = (priceInfo: PriceInfo | Pricing): priceInfo is PriceInfo => {
  return (priceInfo as PriceInfo).priceLabel !== undefined;
};

const UnavailablePriceDescription = ({
  isPostCheckout,
  prescriptionID,
}: {
  readonly isPostCheckout: boolean;
  readonly prescriptionID: number;
}) => {
  const { setActiveActionSheet } = useContext(ActionSheetContext);

  return (
    <Description>
      {PRESCRIPTION_PRICING_LABELS.PRICE_UNAVAILABLE} •{' '}
      <Link
        textSize="mini"
        onPress={() => {
          setActiveActionSheet(
            <PricePendingActionSheet
              orderTypeContext={isPostCheckout ? 'Post checkout' : 'Checkout'}
              prescriptionID={prescriptionID}
            />,
          );
        }}
      >
        Details
      </Link>
    </Description>
  );
};

const PartialFillSavingsDescription = () => {
  return (
    <>
      <Description> • </Description>
      <Description color={COLORS.TEXT_COLORS.SUCCESS}>Paying less for partial fill</Description>
    </>
  );
};

const ProgynyInsuranceDescription = () => {
  return <Description> • Progyny insurance applied</Description>;
};

const PriceDescription = ({
  orderTypeContext,
  prescriptionID,
  delivery,
}: {
  readonly orderTypeContext: OrderTypeContext;
  readonly prescriptionID: number;
  readonly delivery?: LightDelivery;
}) => {
  const isPostCheckout = orderTypeContext === ORDER_TYPE_CONTEXT.POST_CHECKOUT;
  const { shipmentID } = useShipmentID();

  const prescriptionPricing = useSelectorShared((state) => {
    if (isPostCheckout) {
      return getPricingForPrescriptionFromDeliveryItemsForShipment(state, { prescriptionID, shipmentID });
    }
    return getCartSelectedPricingInfoForPrescription(state, prescriptionID);
  });

  if (!prescriptionPricing) {
    return (
      <UnavailablePriceDescription
        isPostCheckout={isPostCheckout}
        prescriptionID={prescriptionID}
      />
    );
  }
  // we get prescription `PriceInfo` when we're in the cart
  if (isPriceInfo(prescriptionPricing)) {
    const { price, priceLabel } = prescriptionPricing;
    if (priceLabel === PRESCRIPTION_PRICING_LABELS.PRICE_UNAVAILABLE || !price) {
      return (
        <UnavailablePriceDescription
          isPostCheckout={false}
          prescriptionID={prescriptionID}
        />
      );
    }

    return (
      <Description>
        {priceLabel} {price}
      </Description>
    );
  }

  // we get prescription `Pricing` when we're in the delivery details
  const pricingInfo = prescriptionPricing.prices.find(({ selected }) => selected);
  const formattedPrice = formatDollars(pricingInfo?.price ?? 0);

  if (prescriptionPricing.scenario === 'unavailable') {
    return (
      <UnavailablePriceDescription
        isPostCheckout
        prescriptionID={prescriptionID}
      />
    );
  }

  return (
    <>
      <Description>{formattedPrice}</Description>
      {prescriptionPricing.scenario === 'progyny' ? <ProgynyInsuranceDescription /> : null}
      {delivery?.med_sync_status === 'short_fill' ? <PartialFillSavingsDescription /> : null}
    </>
  );
};

const SavingsAppliedDescription = ({ prescriptionID }: { readonly prescriptionID: number }) => {
  const prescriptionPricingInfo = useSelectorShared((state) =>
    getCartSelectedPricingInfoForPrescription(state, prescriptionID),
  );
  const { paymentType } = prescriptionPricingInfo || {};
  const priceOptions = useSelectorShared((state) => getPriceOptionsForPrescriptionInOrder(state, { prescriptionID }));
  const cashSavingsText = getCashLessThanInsuranceSavings(priceOptions);
  const savingsApplied = useSelectorShared((state) =>
    getSavingsAppliedAmountForPrescription(state, prescriptionID, paymentType),
  );

  if (cashSavingsText && paymentType === 'without_insurance') {
    return (
      <>
        <Description> • </Description>
        <Description color={COLORS.TEXT_COLORS.SUCCESS}>{cashSavingsText}</Description>
      </>
    );
  }

  if (savingsApplied) {
    return (
      <>
        <Description> • </Description>
        <Description color={COLORS.TEXT_COLORS.SUCCESS}>Savings Applied -{formatDollars(savingsApplied)}</Description>
      </>
    );
  }

  return null;
};

type Props = {
  readonly essentialsHash: Record<string, AddOnOtc>;
  readonly canEditMedication: boolean;
  readonly onChangeEssential?: () => void;
  readonly openEditMedicationFlow?: ({ itemKey, prescriptionID }: { itemKey: ItemKey; prescriptionID: number }) => void;
  readonly orderTypeContext: OrderTypeContext;
  readonly prescriptionID: number;
  readonly delivery?: LightDelivery;
  readonly userID: number;
  readonly includeNewItemTag?: boolean;
};

export const MedicationItem = ({
  essentialsHash,
  onChangeEssential,
  canEditMedication,
  openEditMedicationFlow,
  orderTypeContext,
  prescriptionID,
  delivery,
  userID,
  includeNewItemTag = false,
}: Props) => {
  const windows = useSelectorShared(getWindows);
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const prescription = useSelectorShared((state) => getPrescriptionByID(state, prescriptionID));
  const medication = useSelectorShared((state) => getMedicationForPrescription(state, prescription));
  const userTag = useSelectorShared((state) => getUserTagText(state, { userIDs: [userID] }));
  const nextAvailableDate = useSelectorShared((state) =>
    getEarliestAvailableDateForPrescription__DEPRECATED(state, prescriptionID),
  );
  const hasFamily = useSelectorShared((state) => getHasFamily(state));
  const autobillPrescriptionLoadingByID = useSelectorShared(
    (state) => state.ui.loading.autobillPrescriptionLoadingByID,
  );
  const fetchOrderPricingLoading = useSelectorShared((state) => state.ui.loading.fetchOrderPricingLoading);
  const earliestAvailableReason = useSelectorShared((state) =>
    getEarliestAvailableReasonForPrescription__DEPRECATED(state, prescriptionID),
  );
  const cartAutoRefill = useSelectorShared((state) => getCartAutoRefillForPrescription(state, { prescriptionID }));

  let essential: AddOnOtc | undefined;
  let essentialQuantity;
  if (prescription?.is_add_on_otc && prescription.product_id) {
    essential = essentialsHash[prescription.product_id];
    const productQuantity = essential?.quantity || prescription.quantity;
    essentialQuantity = getItemQuantityForAddOnOtc(prescription, productQuantity);
  }

  if (!medication || !prescription) throw new Error('Invalid medication or prescription');
  const isPostCheckout = orderTypeContext === ORDER_TYPE_CONTEXT.POST_CHECKOUT;
  const isInCheckout = orderTypeContext === ORDER_TYPE_CONTEXT.CHECKOUT;

  const editMedicationOnPress = useCallback(() => {
    if (!canEditMedication) return;
    if (prescriptionID && openEditMedicationFlow) {
      openEditMedicationFlow({
        itemKey: { resource_id: prescriptionID, resource_type: 'Prescription' },
        prescriptionID,
      });
    }
  }, [canEditMedication, openEditMedicationFlow, prescriptionID]);
  const isBackorderdMedication = useBackorderedMedication(prescriptionID);
  const { medicationName } = medication;
  const imageUrl = medication ? getMedImageURL(medication) : null;
  const isAutoRefillEnabled = isPostCheckout ? !!prescription?.auto_refill : !!cartAutoRefill;
  const shouldShowNextAvailableDate =
    !isPostCheckout && !isInCheckout && !prescription.is_add_on_otc && !!nextAvailableDate;

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const buildListItemDescriptions = () => {
    const listItemDescriptions: React.ReactElement<typeof ListDescription>[] = [];

    if (hasFamily) {
      listItemDescriptions.push(<ListDescription iconName="user-small">{userTag}</ListDescription>);
    }

    const autobillPrescriptionLoading = autobillPrescriptionLoadingByID[prescriptionID];

    if (fetchOrderPricingLoading || autobillPrescriptionLoading) {
      listItemDescriptions.push(
        <ListDescription
          iconName="pricetag-small"
          loading
        />,
      );
    } else {
      listItemDescriptions.push(
        <ListDescription iconName="pricetag-small">
          <>
            <PriceDescription
              delivery={delivery}
              orderTypeContext={orderTypeContext}
              prescriptionID={prescriptionID}
            />
            <SavingsAppliedDescription prescriptionID={prescriptionID} />
          </>
        </ListDescription>,
      );
    }

    if (prescription) {
      listItemDescriptions.push(
        <ListDescription iconName="pillbottle-small">
          <>
            {formatQuantity(prescription, delivery)}
            {isPostCheckout && !prescription.is_add_on_otc ? <>&nbsp;&bull;&nbsp;Rx{prescription.rx_number}</> : null}
          </>
        </ListDescription>,
      );
    }

    if (delivery?.med_sync_status === 'short_fill') {
      listItemDescriptions.push(
        <ListDescription iconName="pills-small">
          <>Partial fill ({delivery.days_supply?.toString()} day supply)</>
        </ListDescription>,
      );
    }

    if (!prescription?.is_add_on_otc) {
      if (isAutoRefillEnabled) {
        listItemDescriptions.push(<ListDescription iconName="sync-small">Auto refill</ListDescription>);
      } else {
        listItemDescriptions.push(<ListDescription iconName="sync-small">One-time fill</ListDescription>);
      }
    }

    if (prescription?.is_refrigerated) {
      listItemDescriptions.push(
        <ListDescription
          iconName="snowflake-small"
          type="secondary"
          onPress={() => {
            setActiveActionSheet(<RefrigerationActionSheet medicationName={medicationName} />);
          }}
        >
          Refrigeration required
        </ListDescription>,
      );
    }

    if (prescription?.has_ancillary_items) {
      listItemDescriptions.push(
        <ListDescription
          iconName="syringe-small"
          type="secondary"
          onPress={() => {
            setActiveActionSheet(
              <AncillaryItemsActionSheet
                medicationName={medicationName}
                prescriptionID={prescriptionID}
              />,
            );
          }}
        >
          Supplies included
        </ListDescription>,
      );
    }

    if (prescription && isControl(prescription)) {
      listItemDescriptions.push(
        <ListDescription
          iconName="signature-small"
          type="secondary"
          onPress={() => {
            setActiveActionSheet(<SignatureRequiredActionSheet medicationName={medicationName} />);
          }}
        >
          Signature required upon delivery
        </ListDescription>,
      );
    }

    if (isBackorderdMedication) {
      listItemDescriptions.push(
        <BackorderedTag
          origin="Cart"
          prescriptionId={prescriptionID}
        />,
      );
    }

    if (shouldShowNextAvailableDate && !isBackorderdMedication) {
      listItemDescriptions.push(
        <ListDescription
          iconName="calendar-small"
          type="secondary"
          onPress={() => {
            setActiveActionSheet(
              <NextAvailableDateActionSheet
                earliestAvailableDate={nextAvailableDate}
                earliestDateReason={earliestAvailableReason}
                prescriptionID={prescriptionID}
                orderTypeContext={orderTypeContext}
                analyticsOrigin={ORIGIN_NAMES.CART}
              />,
            );
          }}
        >
          {`Available ${formatRelativeDateWithDow(nextAvailableDate)}`}
        </ListDescription>,
      );
    }

    const isToday = isDateToday(parse(nextAvailableDate, DATE_FORMATS.YEAR_MONTH_DAY_DASHED, 0));
    const timeToCutOff = isToday ? formatTimeToCutoff(getTimeToCutoff(windows)) : '';
    if (shouldShowNextAvailableDate && !!timeToCutOff) {
      listItemDescriptions.push(<ListDescription type="success">{`Order ${timeToCutOff}`}</ListDescription>);
    }

    return listItemDescriptions;
  };

  return (
    <>
      <Row>
        <Column>
          {essentialQuantity ? (
            <AddOnsQuantityTag>
              <Tag
                label={essentialQuantity.toString()}
                type="counter"
                circle
              />
            </AddOnsQuantityTag>
          ) : null}
          {imageUrl ? (
            <Illustration
              size="sm"
              source={{ uri: imageUrl }}
              borderRadius={BORDERS.RADIUS.MD.value}
              accessibilityLabel={IMG_ALT_TEXTS.medication}
            />
          ) : (
            <PlaceholderContainer>
              <AltoIcon name="pills-duo" />
            </PlaceholderContainer>
          )}
        </Column>
        <SmSpacing />
        <Column
          flexGrow={1}
          flexShrink={1}
        >
          <ListItem
            tagPlacement="bottom"
            title={medicationName}
            descriptions={buildListItemDescriptions()}
            TitlePressableIcon={
              canEditMedication ? (
                <EditMedicationItemButton
                  essential={essential}
                  editMedicationOnPress={editMedicationOnPress}
                  onChangeEssential={onChangeEssential}
                />
              ) : undefined
            }
            fullBleed
            tags={
              includeNewItemTag
                ? [
                    <Tag
                      type="recommended"
                      key="new-item"
                      label="new item"
                    />,
                  ]
                : undefined
            }
          />
        </Column>
      </Row>
      <SmSpacing />
      {prescriptionID && userID ? (
        <ManualCouponInfoBox
          prescriptionID={prescriptionID}
          prescriptionUserID={userID}
        />
      ) : null}
    </>
  );
};
